import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BuilderCtx } from "./context";
import { addClone, emptyOverrides, normalizeOverrides, setProp as setOvProp } from "./overrides";
import AdminLogin from "@/components/AdminLogin";
import { Landing } from "@/Landing";
import TopBar from "./TopBar";
import Canvas from "./Canvas";
import LayersPanel from "./LayersPanel";
import PropertiesPanel from "./PropertiesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHORTCUTS = [
  ["Cmd/Ctrl + Z", "Undo"],
  ["Cmd/Ctrl + Shift + Z · Ctrl + Y", "Redo"],
  ["Cmd/Ctrl + S", "Save draft"],
  ["Cmd/Ctrl + P", "Toggle preview"],
  ["Cmd/Ctrl + Shift + P", "Publish"],
  ["Cmd/Ctrl + D", "Duplicate element"],
  ["Cmd/Ctrl + H", "Hide / show element"],
  ["Enter", "Edit text inline"],
  ["Arrow Up", "Select parent element"],
  ["Delete / Backspace", "Delete element"],
  ["Esc", "Deselect"],
  ["?", "Shortcut help"],
];

export default function BuilderPage() {
  const [authed, setAuthed] = useState(false);
  const [overrides, setOverrides] = useState(null);
  const [selected, setSelected] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [remountKey, setRemountKey] = useState(0);
  const [tick, setTick] = useState(0);
  const [saving, setSaving] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [, force] = useState(0);

  const rootRef = useRef(null);
  const ovRef = useRef(null);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const selectedRef = useRef(null);
  const previewRef = useRef(false);

  useEffect(() => { ovRef.current = overrides; }, [overrides]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  useEffect(() => { previewRef.current = preview; }, [preview]);

  useEffect(() => {
    const onBlur = () => {
      setTimeout(() => {
        if (document.activeElement?.tagName === "IFRAME" && !previewRef.current) window.focus();
      }, 50);
    };
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const load = useCallback(async (k) => {
    try {
      const r = await fetch(`${API}/builder/draft`, { headers: { "x-admin-key": k } });
      if (!r.ok) throw new Error(r.status === 401 ? "Wrong admin key" : `Load failed (${r.status})`);
      const ov = normalizeOverrides(await r.json());
      localStorage.setItem("vsl_admin_key", k);
      ovRef.current = ov;
      setOverrides(ov);
      setAuthed(true);
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  useEffect(() => {
    const k = localStorage.getItem("vsl_admin_key");
    if (k) load(k);
  }, [load]);

  const commit = useCallback((next, structural = false) => {
    pastRef.current.push({ ov: ovRef.current, structural });
    if (pastRef.current.length > 50) pastRef.current.shift();
    futureRef.current = [];
    ovRef.current = next;
    setOverrides(next);
    if (structural) setRemountKey((k) => k + 1);
    force((x) => x + 1);
  }, []);

  const undo = useCallback(() => {
    const e = pastRef.current.pop();
    if (!e) return;
    futureRef.current.unshift({ ov: ovRef.current, structural: e.structural });
    ovRef.current = e.ov;
    setOverrides(e.ov);
    setSelected(null);
    if (e.structural) setRemountKey((k) => k + 1);
    force((x) => x + 1);
  }, []);

  const redo = useCallback(() => {
    const e = futureRef.current.shift();
    if (!e) return;
    pastRef.current.push({ ov: ovRef.current, structural: e.structural });
    ovRef.current = e.ov;
    setOverrides(e.ov);
    setSelected(null);
    if (e.structural) setRemountKey((k) => k + 1);
    force((x) => x + 1);
  }, []);

  const qSel = (eid) => rootRef.current?.querySelector(`[data-eid="${CSS.escape(eid)}"]`);

  const selectParent = useCallback(() => {
    const sel = selectedRef.current;
    const el = sel && qSel(sel);
    if (!el) return;
    let p = el.parentElement;
    while (p && !p.dataset?.eid) p = p.parentElement;
    if (p?.dataset?.eid && p.dataset.eid !== sel) setSelected(p.dataset.eid);
  }, []);

  const duplicate = useCallback(() => {
    const sel = selectedRef.current;
    const el = sel && qSel(sel);
    if (!el) return;
    if ((ovRef.current.clones || []).some((c) => c.eid === sel)) {
      toast.error("Duplicate the original element instead of a copy");
      return;
    }
    const newEid = `${sel}-copy-${Date.now().toString(36)}`;
    const c = el.cloneNode(true);
    c.dataset.eid = newEid;
    el.parentElement.insertBefore(c, el.nextSibling);
    commit(addClone(ovRef.current, sel, newEid), true);
    setSelected(newEid);
  }, [commit]);

  const toggleHide = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel) return;
    const cfg = ovRef.current.elements?.[sel] || {};
    commit(setOvProp(ovRef.current, sel, "hidden", !cfg.hidden));
  }, [commit]);

  const removeSelected = useCallback(() => {
    const sel = selectedRef.current;
    if (!sel) return;
    commit(setOvProp(ovRef.current, sel, "deleted", true), true);
    setSelected(null);
  }, [commit]);

  const logout = useCallback(() => {
    localStorage.removeItem("vsl_admin_key");
    window.location.reload();
  }, []);

  const save = useCallback(async () => {
    setSaving("saving");
    const attempt = () =>
      fetch(`${API}/builder/draft`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-admin-key": localStorage.getItem("vsl_admin_key") || "" },
        body: JSON.stringify(ovRef.current),
      });
    try {
      let r;
      try {
        r = await attempt();
      } catch {
        await new Promise((res) => setTimeout(res, 2500));
        r = await attempt();
      }
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(
        e.message === "Failed to fetch"
          ? "Can't reach the server — it may be mid-deploy. Wait a few seconds and try again."
          : e.message
      );
    } finally {
      setSaving("");
    }
  }, []);

  const publish = useCallback(async () => {
    setSaving("publishing");
    const attempt = async () => {
      await fetch(`${API}/builder/draft`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-admin-key": localStorage.getItem("vsl_admin_key") || "" },
        body: JSON.stringify(ovRef.current),
      });
      const r = await fetch(`${API}/builder/publish`, {
        method: "POST",
        headers: { "x-admin-key": localStorage.getItem("vsl_admin_key") || "" },
      });
      if (!r.ok) throw new Error(`Publish failed (${r.status})`);
    };
    try {
      try {
        await attempt();
      } catch (e) {
        if (e.message !== "Failed to fetch") throw e;
        await new Promise((res) => setTimeout(res, 2500));
        await attempt();
      }
      toast.success("Published — the live site now shows your edits");
    } catch (e) {
      toast.error(
        e.message === "Failed to fetch"
          ? "Can't reach the server — it may be mid-deploy. Wait a few seconds and hit Publish again."
          : e.message
      );
    } finally {
      setSaving("");
    }
  }, []);

  const resetAll = useCallback(() => {
    if (window.confirm("Discard ALL visual edits and restore the original site?")) {
      commit(emptyOverrides(), true);
      setSelected(null);
    }
  }, [commit]);

  useEffect(() => {
    const h = (e) => {
      const tag = document.activeElement?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.isContentEditable;
      const mod = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (e.key === "Escape") {
        setSelected(null);
        setShowHelp(false);
        return;
      }
      if (typing) return;
      if (mod && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((mod && k === "z" && e.shiftKey) || (mod && k === "y")) { e.preventDefault(); redo(); }
      else if (mod && k === "s") { e.preventDefault(); save(); }
      else if (mod && k === "p" && e.shiftKey) { e.preventDefault(); publish(); }
      else if (mod && k === "p") { e.preventDefault(); setPreview((p) => !p); }
      else if (mod && k === "d" && selectedRef.current) { e.preventDefault(); duplicate(); }
      else if (mod && k === "h" && selectedRef.current) { e.preventDefault(); toggleHide(); }
      else if (e.key === "Enter" && selectedRef.current) { e.preventDefault(); window.dispatchEvent(new CustomEvent("builder-inline-edit")); }
      else if (e.key === "ArrowUp" && selectedRef.current) { e.preventDefault(); selectParent(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && selectedRef.current) { e.preventDefault(); removeSelected(); }
      else if (e.key === "?") { setShowHelp((s) => !s); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [undo, redo, save, publish, duplicate, toggleHide, removeSelected, selectParent]);

  const api = {
    overrides, ovRef, selected, select: setSelected, device, setDevice,
    editing, setEditing, preview, setPreview, commit, undo, redo,
    remountKey, rootRef, tick, bump: () => setTick((t) => t + 1),
    save, publish, resetAll, saving, logout,
    selectParent, duplicate, toggleHide, removeSelected,
    showHelp, setShowHelp,
    canUndo: pastRef.current.length > 0, canRedo: futureRef.current.length > 0,
  };

  if (!authed || !overrides) {
    return <AdminLogin title="Visual Builder" onSuccess={(c) => load(c)} />;
  }

  return (
    <BuilderCtx.Provider value={api}>
      <div className="fixed inset-0 z-[200] flex flex-col bg-[#0b0b0c] font-sans text-white" data-testid="builder-mode">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          {!preview && <LayersPanel />}
          <Canvas>
            <Landing key={remountKey} />
          </Canvas>
          {!preview && <PropertiesPanel />}
        </div>
        {showHelp && (
          <div className="absolute right-4 top-14 z-[300] w-80 border border-white/10 bg-[#161618] p-4 shadow-2xl" data-testid="shortcuts-modal">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-400">Keyboard shortcuts</p>
            {SHORTCUTS.map(([keys, label]) => (
              <div key={keys} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 text-xs last:border-0">
                <span className="text-neutral-400">{label}</span>
                <kbd className="whitespace-nowrap border border-white/15 bg-white/5 px-1.5 py-0.5 text-[10px] text-white">{keys}</kbd>
              </div>
            ))}
          </div>
        )}
      </div>
    </BuilderCtx.Provider>
  );
}
